// src/utils/voz.js
// ============================================================================
// Sistema global de VOZ (texto → voz) — lee en voz alta las notificaciones.
// ----------------------------------------------------------------------------
// Usa la API nativa del navegador (SpeechSynthesis): NO descarga ningún modelo,
// no llama a ninguna API externa y no cuesta nada (Q0). La preferencia
// encendido/apagado se guarda por dispositivo en localStorage (igual que el
// tema claro/oscuro) — no toca Firestore.
//
// Uso:
//   import voz from '../../utils/voz';
//   voz.hablar('Pedido 26FPC1-1 entregado');   // habla solo si está activada
//   voz.setActiva(true);                        // enciende (y confirma hablando)
//
// 🔊 Gotcha de los navegadores: el audio suele necesitar que el usuario haya
// interactuado con la página antes de sonar. Por eso `setActiva(true)` se llama
// desde el clic del toggle y confirma con una frase corta: ese mismo gesto
// desbloquea el motor de voz para el resto de la sesión.
// ============================================================================

const CLAVE = 'vozNotificaciones';
// Edge entrecorta las voces que llegan por streaming; se trata distinto (ver puntaje()).
const esEdge = typeof navigator !== 'undefined' && /Edg\//.test(navigator.userAgent);

let vozElegida = null;
// Cuántas lecturas tenemos en curso/esperando (para no encimar ni atropellar).
let pendientes = 0;

// Soporte del navegador.
function disponible() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

// Voces en español disponibles en este dispositivo, de mejor a peor.
function listarVoces() {
  if (!disponible()) return [];
  const voces = window.speechSynthesis.getVoices() || [];
  const enEspanol = voces.filter((v) => (v.lang || '').toLowerCase().startsWith('es'));
  return (enEspanol.length ? enEspanol : voces).slice().sort((a, b) => puntaje(b) - puntaje(a));
}

// 🔴 Prioridad de voces — se elige SOLA (no hay selector en pantalla a propósito):
// primero las "Natural" (las de calidad humana), después las de Google (las
// mejores en Chrome) y como respaldo las del equipo.
//
// Las "Natural" de Edge llegan por streaming y ANTES se entrecortaban, por eso
// hubo un momento en que se preferían las locales robóticas. Ya no hace falta:
// con el texto encadenado por comas (ver limpiar()) se escuchan completas —
// probado en Edge, jul 2026. Ojo: las voces naturales que Windows instala para
// el NARRADOR no se le prestan al navegador; las que sí se ven son las "Online".
function puntaje(v) {
  const nombre = (v.name || '').toLowerCase();
  const lang = (v.lang || '').toLowerCase().replace('_', '-');
  let p = 0;
  if (nombre.includes('natural')) p += 200;     // calidad humana
  if (nombre.includes('google')) p += 180;      // en Chrome son excelentes
  if (nombre.includes('premium') || nombre.includes('enhanced')) p += 60;
  if (v.localService) p += 40;                  // respaldo: siempre disponibles
  const orden = ['es-gt', 'es-mx', 'es-419', 'es-us', 'es-co', 'es-ar', 'es-es'];
  const i = orden.indexOf(lang);
  if (i >= 0) p += (orden.length - i) * 5;
  else if (lang.startsWith('es')) p += 2;
  return p;
}

// Elige sola la mejor voz disponible según el puntaje de arriba.
function elegirVoz() {
  if (!disponible()) return null;
  const voces = window.speechSynthesis.getVoices();
  if (!voces || voces.length === 0) return null;
  return listarVoces()[0] || null;
}

// La lista de voces puede llegar vacía al inicio: el navegador avisa cuando carga.
if (disponible()) {
  vozElegida = elegirVoz();
  window.speechSynthesis.addEventListener?.('voiceschanged', () => {
    vozElegida = elegirVoz();
  });
}

// Deja el texto "hablable": sin emojis, sin markdown, sin URLs y no muy largo
// (los textos largos se cortan solos en algunos navegadores).
//
// 🔴 CLAVE DEL ENTRECORTADO: el motor recorta el arranque de audio DESPUÉS DE
// CADA PAUSA FUERTE (punto, signos, salto de línea) — por eso se perdía el
// principio de cada oración, no solo el de la primera. Por eso toda la
// puntuación que corta el habla se normaliza a COMA: la coma encadena las
// frases con una pausa suave, sin cortar el audio, y así se escucha todo.
function limpiar(texto) {
  return String(texto || '')
    .replace(/https?:\/\/\S+/g, '')                                  // enlaces
    .replace(/[*_`#>|]/g, ' ')                                       // markdown
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, ' ') // emojis
    .replace(/[\r\n]+/g, ', ')                                       // saltos de línea
    .replace(/[.;:!?¡¿]+/g, ',')                                     // pausas fuertes → coma
    .replace(/\s*,\s*(,\s*)+/g, ', ')                                // comas repetidas
    .replace(/\s+/g, ' ')
    .replace(/^[\s,]+/, '')                                          // no arrancar con coma
    .trim()
    .replace(/,$/, '')
    .slice(0, 240);
}

function activa() {
  if (!disponible()) return false;
  try {
    return localStorage.getItem(CLAVE) === '1';
  } catch {
    return false;
  }
}

function callar() {
  if (disponible()) {
    try { window.speechSynthesis.cancel(); } catch { /* sin voz: se ignora */ }
  }
  pendientes = 0;
}

// Arma un mensaje con la voz elegida.
function crearMensaje(texto) {
  const u = new SpeechSynthesisUtterance(texto);
  if (!vozElegida) vozElegida = elegirVoz();
  if (vozElegida) u.voice = vozElegida;
  u.lang = vozElegida?.lang || 'es-GT';
  u.rate = 1;
  u.pitch = 1;
  return u;
}

// 🔴 Gotcha del navegador (se comía el arranque: "otificación" en vez de
// "Notificación"): al abrir el audio, el motor pierde el primer fonema. La cura
// real NO es esperar más, sino **sacrificar un mensaje mudo primero**: se encola
// un mensaje con volumen 0 que absorbe ese arranque, y el texto real ya sale
// completo. Solo hace falta cuando el motor está frío (nada sonando).
// 🔴 PREFIJO SACRIFICABLE — no es un capricho, es la mitigación de un defecto
// EXTERNO: el motor de voz de Windows/Edge recorta el arranque de cada síntesis.
// Diagnóstico hecho (jul 2026): pasa igual en la demo oficial de Mozilla, o sea
// NO es de este código; en Android la misma pantalla se escucha perfecta.
// Descartados por no servir: esperar más antes de hablar, mensaje mudo de
// calentamiento, cambiar de voz (fallan todas) y mantener el audio despierto
// con ruido inaudible. Como no se puede evitar que recorte, se le da algo
// DESCARTABLE para que recorte, y así el título y el mensaje llegan completos.
// Si algún día Windows/Edge lo corrigen, se borra esta constante y listo.
// Colchón descartable para el recorte del PRIMER arranque (el de las oraciones
// siguientes se resuelve en limpiar(), encadenando todo con comas).
// 🔴 Termina en COMA a propósito: con punto generaría una pausa fuerte y el
// motor volvería a recortar justo el inicio del mensaje real.
const PREFIJO = esEdge ? 'Atención, atención, ' : 'Atención, ';

function encolar(limpio) {
  const s = window.speechSynthesis;
  try { s.resume(); } catch { /* algunos navegadores no lo exponen */ }

  const u = crearMensaje(PREFIJO + limpio);
  pendientes++;
  const soltar = () => { pendientes = Math.max(0, pendientes - 1); };
  u.onend = soltar;
  u.onerror = soltar;
  s.speak(u);               // se ENCOLA: no corta lo que se está leyendo
}

// Habla el texto. Solo suena si la voz está activada, salvo { forzar: true }.
//
// Comportamiento con varias notificaciones:
//  - Si llegan VARIAS a la vez, quien llama ya manda una sola frase ("Tenés N…").
//  - Si llega una MIENTRAS se está leyendo otra, se ENCOLA (no se corta la
//    anterior: se leen una tras otra, sin encimarse).
//  - Si se acumulan 3 o más, se descarta la cola y se dice un resumen corto,
//    para no dejar al colaborador escuchando un monólogo.
//  - { interrumpir: true } corta lo que haya y habla ya (confirmación del toggle).
function hablar(texto, opciones = {}) {
  if (!disponible()) return false;
  if (!opciones.forzar && !activa()) return false;
  const limpio = limpiar(texto);
  if (!limpio) return false;
  try {
    const s = window.speechSynthesis;

    if (opciones.interrumpir) {
      s.cancel();
      pendientes = 0;
      setTimeout(() => encolar(limpio), 200);   // dejar que el cancel termine
      return true;
    }

    if (pendientes >= 2) {                      // se están amontonando
      s.cancel();
      pendientes = 0;
      setTimeout(() => encolar('Tenés varias notificaciones nuevas.'), 200);
      return true;
    }

    encolar(limpio);
    return true;
  } catch {
    return false;
  }
}

function setActiva(valor) {
  if (!disponible()) return false;
  try {
    localStorage.setItem(CLAVE, valor ? '1' : '0');
  } catch { /* sin localStorage: vale solo para esta sesión */ }
  // El clic que enciende la voz es el gesto que el navegador exige para poder
  // reproducir audio: se aprovecha para confirmar hablando.
  if (valor) hablar('Voz activada', { forzar: true, interrumpir: true });
  else callar();
  return true;
}

const voz = { disponible, activa, setActiva, hablar, callar };

export default voz;
